import { useEffect, useState } from 'react'
import { useJobStore } from '@/store/jobStore'
import { getWebSocketService } from '@/services/websocket'
import type { Job } from '@/types/job'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Hook to connect WebSocket for multiple jobs (used in JobQueuePage)
 * Automatically connects to all active jobs and updates them in real-time
 */
export function useJobsWebSocket(jobIds: string[]) {
  const { loadJobs } = useJobStore()
  const queryClient = useQueryClient()
  const [connectedJobs, setConnectedJobs] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (jobIds.length === 0) {
      setConnectedJobs(new Set())
      return
    }

    const wsService = getWebSocketService()
    const cleanups: (() => void)[] = []

    // Connect to each active job (async)
    Promise.all(
      jobIds.map((jobId) =>
        wsService.connect(jobId, (updatedJob: Job) => {
          // Update in store
          loadJobs()
          
          // Update React Query cache
          queryClient.setQueryData(['jobs', jobId], updatedJob)
          queryClient.invalidateQueries({ queryKey: ['jobs', jobId] })
          queryClient.invalidateQueries({ queryKey: ['jobs'] })
        })
      )
    ).then((cleanupFunctions) => {
      cleanupFunctions.forEach((cleanup) => {
        if (cleanup) cleanups.push(cleanup)
      })

      // Track connection status
      jobIds.forEach((jobId) => {
        if (wsService.isConnected(jobId)) {
          setConnectedJobs((prev) => new Set(prev).add(jobId))
        }
      })
    }).catch((error) => {
      console.error('Failed to connect WebSockets:', error)
    })

    // Subscribe to status changes
    const statusCleanup = wsService.onStatusChange(() => {
      const connected = new Set<string>()
      jobIds.forEach((jobId) => {
        if (wsService.isConnected(jobId)) {
          connected.add(jobId)
        }
      })
      setConnectedJobs(connected)
    })

    return () => {
      cleanups.forEach((cleanup) => cleanup())
      statusCleanup()
    }
  }, [jobIds.join(','), loadJobs, queryClient])

  return {
    connectedJobs: Array.from(connectedJobs),
    isAnyConnected: connectedJobs.size > 0,
  }
}
